import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import ErrorWarningIcon from "components/common/InputErrorWarningIcon";
import { formatPhoneFaxNumber } from "helpers";
import { Fragment, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { SignUpForm } from "types";
import { buttonVariants } from "utils/muiTailwindStyles";

import cites from "./cites.json";
import states from "./states.json";

interface Props {
  onNextStep: () => void;
}

function PracticeDetails({ onNextStep }: Props) {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<SignUpForm>();

  // Watch only required fields - more efficient than watching all form
  const watchedValues = watch([
    "practiceName",
    "practiceEmail",
    "address",
    "street",
    "city",
    "state",
    "zip",
    "primaryPhoneNumber",
  ]);

  // Watch state field to control city field availability
  const selectedCity = watchedValues[4];
  const selectedState = watchedValues[5];

  // Memoize button state calculation to avoid unnecessary re-calculations
  const isButtonEnabled = useMemo(() => {
    const [
      practiceName,
      practiceAddress,
      address,
      street,
      city,
      state,
      zip,
      primaryPhoneNumber,
    ] = watchedValues;

    // Check if all required fields have values
    const hasAllValues = Boolean(
      practiceName &&
        practiceAddress &&
        address &&
        street &&
        city &&
        state &&
        zip &&
        primaryPhoneNumber
    );

    // Check if any required field has errors
    const hasErrors = Boolean(
      errors.practiceName ||
        errors.practiceName ||
        errors.address ||
        errors.street ||
        errors.city ||
        errors.state ||
        errors.zip ||
        errors.primaryPhoneNumber
    );

    return hasAllValues && !hasErrors;
  }, [
    watchedValues,
    errors.practiceName,
    errors.address,
    errors.street,
    errors.city,
    errors.state,
    errors.zip,
    errors.primaryPhoneNumber,
  ]);

  const errorIcon = (name: keyof SignUpForm) => {
    const isError = !!errors[name];
    if ((name === "city" || name === "state") && isError) {
      return {
        popupIcon: <ErrorWarningIcon hasError={isError} />,
      };
    }
    return {
      endAdornment: <ErrorWarningIcon hasError={isError} />,
    };
  };

  return (
    <Fragment>
      <Box className="text-center space-y-6">
        <Typography
          variant="h5"
          component="h5"
          className="text-f22 leading-7 text-surface-dark"
        >
          Sign Up
        </Typography>
        <Typography variant="body2" className="text-black text-base">
          Enter your practice details to set up your account
        </Typography>
      </Box>

      <Box className="space-y-6">
        <Controller
          name="practiceName"
          control={control}
          render={({ field }) => (
            <TextField
              id="practice-name"
              label="Practice Name"
              variant="outlined"
              className="w-full"
              error={!!errors.practiceName}
              helperText={errors.practiceName?.message}
              slotProps={{
                input: errorIcon(field.name),
                htmlInput: {
                  maxLength: 255,
                },
              }}
              {...field}
            />
          )}
        />

        <Controller
          name="practiceEmail"
          control={control}
          render={({ field }) => (
            <TextField
              id="practice-email"
              label="Practice Email"
              variant="outlined"
              className="w-full"
              error={!!errors.practiceEmail}
              helperText={errors.practiceEmail?.message}
              slotProps={{
                input: errorIcon(field.name),
                htmlInput: {
                  maxLength: 255,
                },
              }}
              {...field}
            />
          )}
        />

        <Box className="flex gap-5">
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                id="address"
                label="Address"
                variant="outlined"
                className="w-full"
                error={!!errors.address}
                helperText={errors.address?.message}
                slotProps={{
                  input: errorIcon(field.name),
                }}
                {...field}
              />
            )}
          />

          <Controller
            name="street"
            control={control}
            render={({ field }) => {
              const isStreetDisabled = !selectedCity;
              return (
                <TextField
                  id="street"
                  label="Street"
                  variant="outlined"
                  className="w-full"
                  disabled={isStreetDisabled}
                  error={!!errors.street}
                  helperText={errors.street?.message}
                  classes={{
                    root: isStreetDisabled ? "opacity-50" : "",
                  }}
                  slotProps={{
                    input: errorIcon(field.name),
                  }}
                  {...field}
                />
              );
            }}
          />
        </Box>

        <Controller
          name="city"
          control={control}
          render={({ field }) => {
            const cityOptions = selectedState
              ? (cites as Record<string, string[]>)[selectedState] || []
              : [];
            const isCityDisabled = !selectedState;

            return (
              <Autocomplete
                disablePortal
                disabled={isCityDisabled}
                options={cityOptions}
                className="w-full"
                getOptionLabel={(option) => option}
                onChange={(_, value) => field.onChange(value)}
                value={field.value || null}
                classes={{
                  root: isCityDisabled ? "opacity-50" : "",
                }}
                {...errorIcon(field.name)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    variant="outlined"
                    id="city"
                    error={!!errors.city}
                    helperText={errors.city?.message}
                  />
                )}
              />
            );
          }}
        />

        <Controller
          name="state"
          control={control}
          render={({ field }) => {
            return (
              <Autocomplete
                disablePortal
                options={states}
                className="w-full"
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.abbreviation}
                {...errorIcon(field.name)}
                onChange={(_, value) => {
                  field.onChange(value?.label);
                  // Clear city field when state changes
                  if (field.value !== value?.label) {
                    setValue("city", "");
                  }
                }}
                value={
                  states.find((state) => state.label === field.value) || null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    variant="outlined"
                    id="state"
                    error={!!errors.state}
                    helperText={errors.state?.message}
                  />
                )}
              />
            );
          }}
        />

        <Controller
          name="zip"
          control={control}
          render={({ field }) => (
            <TextField
              id="zip"
              label="Zip"
              variant="outlined"
              className="w-full"
              error={!!errors.zip}
              helperText={errors.zip?.message}
              slotProps={{
                input: errorIcon(field.name),
                htmlInput: {
                  maxLength: 5,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                },
              }}
              {...field}
            />
          )}
        />

        <Box className="flex gap-5">
          <Controller
            name="primaryPhoneNumber"
            control={control}
            render={({ field }) => (
              <TextField
                id="primary-phone-number"
                label="Primary Phone Number"
                variant="outlined"
                className="w-full"
                error={!!errors.primaryPhoneNumber}
                helperText={errors.primaryPhoneNumber?.message}
                slotProps={{
                  input: errorIcon(field.name),
                  htmlInput: {
                    maxLength: 12,
                    inputMode: "numeric",
                    pattern: "[0-9-]*",
                  },
                }}
                onChange={(e) => {
                  const formattedValue = formatPhoneFaxNumber(e.target.value);
                  const numbersOnly = formattedValue.replace(/[^0-9]/g, "");
                  field.onChange(numbersOnly);
                }}
                value={formatPhoneFaxNumber(field.value || "")}
              />
            )}
          />

          <Controller
            name="faxNumber"
            control={control}
            render={({ field }) => (
              <TextField
                id="fax-number"
                label="Fax Number (optional)"
                variant="outlined"
                className="w-full"
                error={!!errors.faxNumber}
                helperText={errors.faxNumber?.message}
                slotProps={{
                  input: errorIcon(field.name),
                  htmlInput: {
                    maxLength: 12,
                    inputMode: "numeric",
                    pattern: "[0-9-]*",
                  },
                }}
                onChange={(e) => {
                  // Format for display but store only numbers
                  const formattedValue = formatPhoneFaxNumber(e.target.value);
                  const numbersOnly = formattedValue.replace(/[^0-9]/g, "");
                  field.onChange(numbersOnly);
                }}
                value={formatPhoneFaxNumber(field.value || "")}
              />
            )}
          />
        </Box>

        <Button
          variant="contained"
          className={`${buttonVariants.primary.className} w-full text-base rounded-full py-3 disabled:text-surface-light disabled:opacity-40`}
          sx={{
            ...buttonVariants.primary.sx,
          }}
          disabled={!isButtonEnabled}
          onClick={onNextStep}
        >
          Confirm Information
        </Button>
      </Box>
    </Fragment>
  );
}

export default PracticeDetails;
